import { Component, Input, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import moment from 'moment';
import { BaseComponent } from '../base-form/base-form.component';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-update-status-group-lesson',
  templateUrl: './update-status-group-lesson.component.html',
  styleUrls: ['./update-status-group-lesson.component.css']
})
export class UpdateStatusGroupLessonComponent extends BaseComponent implements OnInit {
  @Input() appointmentType: string;
  @Input() eventID: number;

  attendees: any[] = [];
  isLoading = false;
  groupLessonData: any = {};
  lessonStatuses: any[] = [];
  isSaving: boolean = false;
  successMessageVisible: boolean = false;
  private existingAttendeeIds: string[] = [];

  ngOnInit() {
    this.lessonStatuses = this._taxonomyService.lesson_status;
    this.loadEventData();
  }

  loadEventData() {
    this.successMessage = '';
    this.errorMessage = '';
    this.isLoading = true;
    this._entityRESTService.getEntity('events', 'group_lesson', this.eventID)
      .subscribe(
        (data: any) => {
          // Process the dates and times
          const startDate = moment(data.field_date_and_time);
          const duration = data.field_duration?.split(':') || ['00', '45'];
          const durationMinutes = parseInt(duration[0]) * 60 + parseInt(duration[1]);
          const endDate = moment(startDate).add(durationMinutes, 'minutes');

          this.groupLessonData = {
            date: startDate.format('LL'),
            startTime: startDate.format('h:mm A'),
            endTime: endDate.format('h:mm A'),
            instructor: data.field_instructor?.name || 'Not Assigned'
          };

          // Process students into a flat list of individual attendees for the status UI.
          //
          // Each attendee entity may have one or two students in field_students.
          // New records (created after the couple-split feature) will always have one
          // student each. Legacy records may still have two (a couple on one record).
          //
          // We flatten couples into separate UI rows so each student gets their own
          // status dropdown. The is_couple flag tracks legacy records so onSubmit()
          // knows to split them into separate entities when saving.
          if (data.field_students) {
            const studentsArray = Array.isArray(data.field_students)
              ? data.field_students
              : [data.field_students];

            // Track all existing attendee entity IDs. Needed when splitting couples
            // to update the event's field_students reference list with new IDs.
            this.existingAttendeeIds = studentsArray.map(s => s.id);

            this.attendees = studentsArray.flatMap(student => {
              const studentIds = Array.isArray(student.field_students)
                ? student.field_students
                : [student.field_students];

              const isCouple = studentIds.length > 1;

              // Split the attendee title by comma to get individual names.
              // Drupal's presave builds the title from all contacts on the account,
              // so for couples the title is "Name1, Name2". We use position to map names.
              return studentIds.map(studentId => ({
                attendees_id: student.id,
                name: student.title.split(', ')[studentIds.indexOf(studentId)] || student.title,
                status: student.field_status || '64',
                student_id: studentId,
                is_couple: isCouple,
                // Preserve these fields. Needed to create the new attendee entity
                // when splitting a legacy couple record on save.
                field_student_account: student.field_student_account,
                field_enrollment: student.field_enrollment,
                field_studio_reference: student.field_studio_reference,
              }));
            });
          } else {
            this.attendees = [];
            this.existingAttendeeIds = [];
          }
          this.isLoading = false;
        },
        error => {
          LoggerService.error('Error loading group lesson data:', error);
          this.errorMessage = 'Error loading group lesson data';
          this.isLoading = false;
        }
      );
  }

  /**
   * Save attendee statuses, handling both new (individual) and legacy (couple) records.
   *
   * New records: one attendee entity per student; just PATCH the status.
   *
   * Legacy couple records: one attendee entity with two students in field_students.
   * The UI shows them as two separate rows (from loadEventData's flatMap), but they
   * share the same attendees_id. To give each student an independent status we must
   * split the record:
   *   1. PATCH the original attendee to keep only the first student + their status
   *   2. POST a new attendee entity for the second student with their own status
   *   3. PATCH the event's field_students to include the newly created attendee ID
   *
   * After this one-time split, subsequent saves will take the simple PATCH path.
   */
  override onSubmit(form: NgForm) {
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Group UI rows by attendees_id. For legacy couples, multiple rows will share
    // the same ID. That's how we detect records that need splitting.
    const grouped: { [id: string]: any[] } = {};
    this.attendees.forEach(a => {
      if (!grouped[a.attendees_id]) grouped[a.attendees_id] = [];
      grouped[a.attendees_id].push(a);
    });

    const updates: Observable<any>[] = [];
    const coupleSplits: { first: any, rest: any[], attendeeId: string }[] = [];

    for (const [attendeeId, rows] of Object.entries(grouped)) {
      if (rows.length === 1 || !rows[0].is_couple) {
        // Normal case: one student per attendee, simple status update.
        updates.push(
          this._entityRESTService.patchEntity(
            'attendees', 'attendance', +attendeeId,
            { field_status: rows[0].status }
          )
        );
      } else {
        // Legacy couple: two UI rows share one attendee entity.
        // Keep the first student on the original entity, split the rest into new entities.
        coupleSplits.push({ first: rows[0], rest: rows.slice(1), attendeeId });
        updates.push(
          this._entityRESTService.patchEntity(
            'attendees', 'attendance', +attendeeId,
            {
              field_students: [rows[0].student_id],
              field_status: rows[0].status
            }
          )
        );
      }
    }

    if (coupleSplits.length === 0) {
      forkJoin(updates).subscribe(
        () => this.onSaveSuccess(),
        error => this.onSaveError(error)
      );
      return;
    }

    // For legacy couple splits: run status updates first, then create new attendee
    // entities for the split-off students, then update the event reference list.
    forkJoin(updates).pipe(
      switchMap(() => {
        const creates: Observable<any>[] = [];
        coupleSplits.forEach(split => {
          split.rest.forEach(student => {
            creates.push(
              this._entityRESTService.createEntity(
                'attendees', 'attendance',
                {
                  field_students: [student.student_id],
                  field_student_account: split.first.field_student_account,
                  field_enrollment: split.first.field_enrollment || '',
                  field_event_reference: this.eventID,
                  field_status: student.status,
                  field_studio_reference: split.first.field_studio_reference,
                }
              )
            );
          });
        });
        return forkJoin(creates);
      }),
      switchMap((newAttendees: any[]) => {
        // The event's field_students must reference all attendee entities.
        // Add the newly created IDs alongside the existing ones.
        const newIds = newAttendees.map(a => a.id);
        const allIds = [...this.existingAttendeeIds, ...newIds];
        return this._entityRESTService.patchEntity(
          'events', 'group_lesson', this.eventID,
          { field_students: allIds }
        );
      })
    ).subscribe(
      () => this.onSaveSuccess(),
      error => this.onSaveError(error)
    );
  }

  private onSaveSuccess() {
    this.isSaving = false;
    this.successMessage = 'Status updated for all attendees';
    this.loadEventData();
  }

  private onSaveError(error: any) {
    this.isSaving = false;
    this.errorMessage = error?.error?.message || "There was a problem updating the status";
  }

  setAllStatus(statusId: string): void {
    this.attendees.forEach(attendee => {
      attendee.status = statusId;
    });
  }

  isButtonDisabled(): boolean {
    return !this.attendees || this.attendees.length === 0;
  }
}
